import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createScenarioAPI,
  deleteScenarioAPI,
  duplicateScenarioAPI,
  getLatestYearMonthAPI,
  saveScenarioDraftAPI,
  ScenarioCreationResponse,
} from './scenarioCreationAPI';
import { hideLoader, showLoader } from '../ui/loaderSlice';
import { RootState } from '../../state/store';

export interface ScenarioCreationState {
  scenarioName: string;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  previousScenarioName: string;
  selectedPvpOrConjointToggle: string;
  saveScenarioDraft: string;
  duplicateScenarioRes: string;
  deleteScenarioRes: string;
  scenario_action: string;
  latest_year_month: any;
}

const initialState: ScenarioCreationState = {
  scenarioName: '',
  previousScenarioName: '',
  loading: false,
  error: null,
  successMessage: null,
  selectedPvpOrConjointToggle: 'pvp',
  saveScenarioDraft: '',
  duplicateScenarioRes: '',
  deleteScenarioRes: '',
  scenario_action: 'new',
  latest_year_month: {},
};

export interface SaveScenarioDraftPayload {
  bottler: string[];
  state: string[];
  category: string[];
  pack_segment: string[];
  pack_size: string[];
  brand: string[];
  price: number;
  distribution: string;
  intervention_type: string;
  mapping: any;
  scenario_name: string;
}

export const createScenario = createAsyncThunk(
  'scenarioCreation/createScenario',
  async (payload: any, { dispatch, rejectWithValue }) => {
    dispatch(showLoader());
    try {
      const response: ScenarioCreationResponse = await createScenarioAPI(payload);
      return response;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || 'Failed to create scenario';
      return rejectWithValue(errorMessage);
    } finally {
      dispatch(hideLoader());
    }
  },
);

export const saveScenarioDraft = createAsyncThunk(
  'scenarioCreation/saveScenarioDraft',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await saveScenarioDraftAPI(payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create scenario draft';
      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteScenario = createAsyncThunk(
  'scenarioCreation/deleteScenario',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await deleteScenarioAPI(payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create scenario draft';
      return rejectWithValue(errorMessage);
    }
  },
);

export const duplicateScenario = createAsyncThunk(
  'scenarioCreation/duplicateScenario',
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await duplicateScenarioAPI(payload);
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create scenario draft';
      return rejectWithValue(errorMessage);
    }
  },
);

export const getLatestYearMonth = createAsyncThunk<{ latest_year_month: string }, void, { rejectValue: string }>(
  'scenarioCreation/getLatestYearMonth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getLatestYearMonthAPI();
      return response;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to create scenario draft';
      return rejectWithValue(errorMessage);
    }
  },
);

const scenarioCreationSlice = createSlice({
  name: 'scenarioCreation',
  initialState,
  reducers: {
    setScenarioName: (state, action: PayloadAction<string>) => {
      state.scenarioName = action.payload;
    },
    resetScenarioName: (state) => {
      state.scenarioName = '';
      state.successMessage = null;
      state.error = null;
    },
    setPvpOrConjointToggle: (state, action: PayloadAction<string>) => {
      state.selectedPvpOrConjointToggle = action.payload;
    },
    setScenarioAction: (state, action: PayloadAction<string>) => {
      state.scenario_action = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createScenario.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.successMessage = null;
    });
    builder.addCase(createScenario.fulfilled, (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
    });
    builder.addCase(createScenario.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Failed to create scenario';
    });
    builder.addCase(saveScenarioDraft.pending, (state) => {
      state.error = null;
      state.successMessage = null;
    });
    builder.addCase(saveScenarioDraft.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.saveScenarioDraft = action.payload;
    });
    builder.addCase(saveScenarioDraft.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Failed to add draft scenario';
    });
    builder.addCase(deleteScenario.pending, (state) => {
      state.error = null;
      state.successMessage = null;
    });
    builder.addCase(deleteScenario.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.deleteScenarioRes = action.payload;
    });
    builder.addCase(deleteScenario.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Failed to delete scenario';
    });

    builder.addCase(duplicateScenario.pending, (state) => {
      state.error = null;
      state.successMessage = null;
    });
    builder.addCase(duplicateScenario.fulfilled, (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.duplicateScenarioRes = action.payload;
    });
    builder.addCase(duplicateScenario.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as string) || 'Failed to delete scenario';
    });

    builder.addCase(getLatestYearMonth.pending, (state) => {
      state.error = null;
      state.successMessage = null;
    });
    builder.addCase(getLatestYearMonth.fulfilled, (state, action) => {
      state.loading = false;
      state.latest_year_month = action.payload;
    });
    builder.addCase(getLatestYearMonth.rejected, (state, action) => {
      state.loading = false;
      state.error = (action.payload as any) || 'Failed to get Latest Year and Month';
    });
  },
});

export const { setScenarioName, resetScenarioName, setPvpOrConjointToggle, setScenarioAction } =
  scenarioCreationSlice.actions;

export const selectScenarioName = (state: RootState) => state.scenarioCreation.scenarioName;
export const selectScenarioLoading = (state: RootState) => state.scenarioCreation.loading;
export const selectScenarioError = (state: RootState) => state.scenarioCreation.error;
export const selectScenarioSuccessMessage = (state: RootState) => state.scenarioCreation.successMessage;
export const selectPvpOrConjointToggle = (state: RootState) => state.scenarioCreation.selectedPvpOrConjointToggle;
export const selectSaveScenarioDraft = (state: RootState) => state.scenarioCreation.saveScenarioDraft;
export const selectDuplicateScenario = (state: RootState) => state.scenarioCreation.duplicateScenarioRes;
export const selectDeleteScenario = (state: RootState) => state.scenarioCreation.deleteScenarioRes;
export const selectScenarioAction = (state: RootState) => state.scenarioCreation.scenario_action;
export const selectLatestYearAndMonth = (state: RootState) => state.scenarioCreation.latest_year_month;

export default scenarioCreationSlice.reducer;
